import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeTypes,
  type EdgeTypes,
  BackgroundVariant,
  Panel,
  SelectionMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { PromptNode } from './nodes/PromptNode';
import { ImageNode } from './nodes/ImageNode';
import { PresetNode } from './nodes/PresetNode';
import { UploadNode } from './nodes/UploadNode';
import { GenerateNode } from './nodes/GenerateNode';
import { NoteNode } from './nodes/NoteNode';
import { VariationNode } from './nodes/VariationNode';
import { TemplateNode } from './nodes/TemplateNode';
import { DeletableEdge } from './edges/DeletableEdge';
import { VariationEdge } from './edges/VariationEdge';
import { CanvasToolbar } from './CanvasToolbar';
import { Spinner } from '../ui/Spinner';
import { canvasService } from '../../services/canvasService';
import { useCanvasSync, type CanvasSyncEvent } from '../../hooks/useCanvasSync';
import { useAuthStore } from '../../stores/authStore';
import { CursorOverlay } from './CursorOverlay';
import type { PresenceUser } from '../../hooks/usePresence';
import type { CanvasData, NodeType, NodeTemplate } from '../../types/canvas';
import { useCanvasHistory } from '../../hooks/useCanvasHistory';
import { SaveAsTemplateModal } from './SaveAsTemplateModal';
import { usePaywallCheck } from '../../hooks/usePaywallCheck';
import { usePaywallStore } from '../../stores/paywallStore';
import { CanvasPaywallOverlay } from '../Paywall/CanvasPaywallOverlay';
import { useLastUsedModelStore } from '../../stores/lastUsedModelStore';
import { useCreditStore } from '../../stores/creditStore';
import { CloudUpload } from 'lucide-react';

const nodeTypes: NodeTypes = {
  prompt: PromptNode,
  image: ImageNode,
  preset: PresetNode,
  upload: UploadNode,
  generate: GenerateNode,
  note: NoteNode,
  variation: VariationNode,
  template: TemplateNode,
};

const edgeTypes: EdgeTypes = {
  deletable: DeletableEdge,
  variation: VariationEdge,
};

interface FlowCanvasProps {
  canvasId: string;
  presenceUsers?: PresenceUser[];
  readOnly?: boolean;
  initialData?: { nodes: any[]; edges: any[]; name: string; viewport?: any };
}

export function FlowCanvas({ canvasId, presenceUsers = [], readOnly = false, initialData }: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner canvasId={canvasId} presenceUsers={presenceUsers} readOnly={readOnly} initialData={initialData} />
    </ReactFlowProvider>
  );
}

// Clipboard state stored outside component to persist across re-renders
let clipboardNodes: Node[] = [];
let clipboardEdges: {
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}[] = [];
// Track edges from external nodes to copied nodes (for reconnecting on paste)
let clipboardExternalEdges: {
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}[] = [];
// Timestamp when nodes were copied - used to prioritize internal clipboard over system clipboard
let clipboardTimestamp: number = 0;

function FlowCanvasInner({ canvasId, presenceUsers = [], readOnly = false, initialData }: FlowCanvasProps) {
  const user = useAuthStore((s) => s.user);
  const lastCursorSentRef = useRef(0);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  // Track which nodes are being dragged by remote users (nodeId -> userId)
  const remoteDragsRef = useRef<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isCanvasDragOver, setIsCanvasDragOver] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reactFlowInstance = useReactFlow();

  // Template modal state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateModalNodeId, setTemplateModalNodeId] = useState<string | null>(null);

  // Paywall state
  const { needsUpgrade, needsTrial, isExhausted, isCancelledTrial, isExhaustedTrial } = usePaywallCheck();
  const openPaywall = usePaywallStore((s) => s.openPaywall);
  const isPaywalled = needsUpgrade || needsTrial || isExhausted || isCancelledTrial || isExhaustedTrial;

  // Track selected nodes for edge highlighting
  const selectedNodeIds = useMemo(() => {
    return new Set(nodes.filter(n => n.selected).map(n => n.id));
  }, [nodes]);

  // Compute edges with highlighting for selected node connections
  const highlightedEdges = useMemo(() => {
    if (selectedNodeIds.size === 0) return edges;
    return edges.map(edge => {
      const isConnectedToSelected = selectedNodeIds.has(edge.source) || selectedNodeIds.has(edge.target);
      if (isConnectedToSelected) {
        return {
          ...edge,
          data: { ...edge.data, highlighted: true },
          zIndex: 1000, // Bring to front
        };
      }
      return edge;
    });
  }, [edges, selectedNodeIds]);

  // Undo/Redo history
  const { pushAction, undo, redo, canUndo, canRedo, isUndoing, isRedoing } = useCanvasHistory();

  // Keep a ref to nodes so callbacks always have access to current state
  const nodesRef = useRef<Node[]>(nodes);
  nodesRef.current = nodes;

  // Keep a ref to edges for undo operations
  const edgesRef = useRef<Edge[]>(edges);
  edgesRef.current = edges;

  // Track node positions at drag start for undo
  const dragStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Ref to hold the quiet reload function (breaks circular dependency)
  const reloadCanvasQuietlyRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Handle edge deletion via button (for mobile)
  const handleDeleteEdge = useCallback(
    async (edgeId: string) => {
      try {
        await canvasService.deleteEdge(canvasId, edgeId);
      } catch (err) {
        console.error('Failed to delete edge:', err);
      }
    },
    [canvasId],
  );

  // Load canvas data
  useEffect(() => {
    loadCanvas();
  }, [canvasId]);

  const convertToFlowData = (data: any) => {
    // Convert backend nodes to React Flow nodes
    const flowNodes: Node[] = data.nodes.map((n: any) => ({
      id: n.id,
      type: n.type,
      position: { x: n.position_x, y: n.position_y },
      data: {
        ...n.data,
        // Clear stale uploading flag if node already has an imageUrl
        uploading: n.data?.uploading && !n.data?.imageUrl ? true : false,
        nodeWidth: n.width || undefined,
        canvasId,
        ...(readOnly ? {} : {
          onUpdate: handleNodeDataUpdate,
          onGenerate: handleGenerate,
          onStopGeneration: handleStopGeneration,
          onUpload: handleUpload,
          onSelectFromGallery: handleSelectFromGallery,
          onDelete: handleDeleteNode,
          onConvertToPreset: handleConvertToPreset,
          onWidthChange: handleWidthChange,
          // Variation-specific callbacks
          ...(n.type === 'generate' ? { onAddVariation: handleAddVariation, onSaveAsTemplate: handleSaveAsTemplate } : {}),
          ...(n.type === 'variation' ? { onGenerateVariation: handleGenerateVariation } : {}),
          ...(n.type === 'template' ? { onGenerateTemplate: handleGenerateTemplate } : {}),
        }),
        readOnly,
      },
    }));

      // Convert backend edges to React Flow edges
      // Variation bonds use the 'variation' edge type (non-deletable)
      const variationNodeIds = new Set(
        data.nodes.filter((n: any) => n.type === 'variation').map((n: any) => n.id),
      );
      const flowEdges: Edge[] = data.edges.map((e: any) => {
        const isVariationBond =
          variationNodeIds.has(e.target_node_id) && e.target_handle === 'variation-target';
        return {
          id: e.id,
          source: e.source_node_id,
          target: e.target_node_id,
          sourceHandle: e.source_handle,
          targetHandle: e.target_handle,
          type: isVariationBond ? 'variation' : 'deletable',
          animated: !isVariationBond,
          style: isVariationBond ? undefined : { stroke: '#10b981', strokeWidth: 2 },
          data: isVariationBond ? { isVariationBond: true } : (readOnly ? {} : { onDelete: handleDeleteEdge }),
        };
      });

    return { flowNodes, flowEdges };
  };

  const loadCanvas = async () => {
    try {
      setIsLoading(true);

      // Use initialData if provided (for shared/read-only canvases)
      if (initialData) {
        const { flowNodes, flowEdges } = convertToFlowData(initialData);
        setNodes(flowNodes);
        setEdges(flowEdges);
        return;
      }

      const { data } = await canvasService.get(canvasId);
      const { flowNodes, flowEdges } = convertToFlowData(data);
      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (err) {
      console.error('Failed to load canvas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle node width changes (horizontal resize)
  const handleWidthChange = useCallback(
    async (nodeId: string, width: number) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, nodeWidth: width } } : n)),
      );
      try {
        await canvasService.updateNode(canvasId, nodeId, { width });
      } catch (err) {
        console.error('Failed to save node width:', err);
      }
    },
    [canvasId],
  );

  // Add a variation node connected to a generate node
  const handleAddVariation = useCallback(
    async (generateNodeId: string) => {
      const generateNode = nodesRef.current.find((n) => n.id === generateNodeId);
      if (!generateNode) return;

      // Count existing variations to fan them out vertically
      const existingVariations = nodesRef.current.filter(
        (n) => n.type === 'variation' && n.data?.parentNodeId === generateNodeId,
      );
      const variationIndex = existingVariations.length;
      const position = {
        x: generateNode.position.x + 320,
        y: generateNode.position.y + variationIndex * 200,
      };

      try {
        // Create the variation node
        const { data } = await canvasService.addNode(canvasId, {
          type: 'variation',
          position_x: position.x,
          position_y: position.y,
          data: { parentNodeId: generateNodeId, variationPrompt: '', status: 'idle' },
        });

        const newNode: Node = {
          id: data.id,
          type: 'variation',
          position,
          data: {
            ...data.data,
            canvasId,
            onUpdate: handleNodeDataUpdate,
            onDelete: handleDeleteNode,
            onGenerateVariation: handleGenerateVariation,
            onWidthChange: handleWidthChange,
          },
        };
        setNodes((nds) => [...nds, newNode]);

        // Create the permanent bond edge
        const edgeId = `edge-${Date.now()}`;
        const newEdge: Edge = {
          id: edgeId,
          source: generateNodeId,
          target: data.id,
          sourceHandle: 'generate-source',
          targetHandle: 'variation-target',
          type: 'variation',
          animated: false,
          data: { isVariationBond: true },
        };
        setEdges((eds) => [...eds, newEdge]);

        // Save edge to backend
        const { data: savedEdge } = await canvasService.addEdge(canvasId, {
          source_node_id: generateNodeId,
          target_node_id: data.id,
          source_handle: 'generate-source',
          target_handle: 'variation-target',
        });
        setEdges((eds) => eds.map((e) => (e.id === edgeId ? { ...e, id: savedEdge.id } : e)));
      } catch (err) {
        console.error('Failed to add variation:', err);
      }
    },
    [canvasId, handleWidthChange],
  );

  // Generate for a variation node (calls the variation generation endpoint)
  const handleGenerateVariation = useCallback(
    async (nodeId: string) => {
      // Paywall guard
      if (isPaywalled) {
        openPaywall('canvas', needsTrial ? 'trial' : isCancelledTrial ? 'cancelled_trial' : isExhaustedTrial ? 'exhausted_trial' : needsUpgrade ? 'upgrade' : 'exhausted');
        return;
      }

      // Set generating status
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, status: 'generating' } } : n)),
      );

      try {
        const { data } = await canvasService.generateVariation(canvasId, nodeId);
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    status: 'complete',
                    imageUrl: data.imageUrl || null,
                    videoUrl: data.videoUrl || null,
                    textContent: data.textContent || null,
                    thumbnailUrl: data.thumbnailUrl || null,
                    error: null,
                  },
                }
              : n,
          ),
        );
        // Refresh credit balance after successful variation generation
        useCreditStore.getState().reset();
        useCreditStore.getState().fetchBalance();
      } catch (err: any) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, status: 'error', error: err.message } }
              : n,
          ),
        );
      }
    },
    [canvasId],
  );

  // Handle template generation
  const handleGenerateTemplate = useCallback(async (nodeId: string) => {
    // Paywall guard
    if (isPaywalled) {
      openPaywall('canvas', needsTrial ? 'trial' : isCancelledTrial ? 'cancelled_trial' : isExhaustedTrial ? 'exhausted_trial' : needsUpgrade ? 'upgrade' : 'exhausted');
      return;
    }

    // Set generating status
    setNodes((nds) =>
      nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, status: 'generating' } } : n))
    );

    try {
      const { data } = await canvasService.generateTemplate(canvasId, nodeId);
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? {
          ...n,
          data: {
            ...n.data,
            status: 'complete',
            imageUrl: data.imageUrl || null,
            videoUrl: data.videoUrl || null,
            textContent: data.textContent || null,
            thumbnailUrl: data.thumbnailUrl || null,
            mediaType: data.textContent ? 'text' : data.videoUrl ? 'video' : 'image',
            error: null,
          },
        } : n))
      );
      // Refresh credit balance after successful template generation
      useCreditStore.getState().reset();
      useCreditStore.getState().fetchBalance();
    } catch (err: any) {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, status: 'error', error: err.message } } : n))
      );
    }
  }, [canvasId]);

  // Handle save as template
  const handleSaveAsTemplate = useCallback((nodeId: string) => {
    setTemplateModalNodeId(nodeId);
    setTemplateModalOpen(true);
  }, []);

  // Callback when template is successfully saved - mark node as template
  const handleTemplateSaved = useCallback(() => {
    if (templateModalNodeId) {
      setNodes((nds) =>
        nds.map((n) => (n.id === templateModalNodeId
          ? { ...n, data: { ...n.data, is_template: true } }
          : n
        ))
      );
    }
  }, [templateModalNodeId]);

  // Handle node data updates (from prompt editing, preset changes, etc.)
  const handleNodeDataUpdate = useCallback(
    async (nodeId: string, updates: any) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, ...updates, data: { ...n.data, ...updates.data } } : n,
        ),
      );

      // Debounced save to backend
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await canvasService.updateNode(canvasId, nodeId, updates);
        } catch (err) {
          console.error('Failed to save node:', err);
        }
      }, 500);
    },
    [canvasId],
  );

  // Handle generation (image, video, or text based on connected preset's mediaType)
  const handleGenerate = useCallback(
    async (nodeId: string) => {
      // Paywall guard — block generation if user needs upgrade or is out of credits
      if (isPaywalled) {
        openPaywall('canvas', needsTrial ? 'trial' : isCancelledTrial ? 'cancelled_trial' : isExhaustedTrial ? 'exhausted_trial' : needsUpgrade ? 'upgrade' : 'exhausted');
        return;
      }

      // Determine mediaType from connected preset or inline settings
      const currentNodes = reactFlowInstance.getNodes();
      const currentEdges = reactFlowInstance.getEdges();
      const incomingEdges = currentEdges.filter((e) => e.target === nodeId);
      const sourceNodeIds = incomingEdges.map((e) => e.source);
      const presetNode = currentNodes.find(
        (n) => sourceNodeIds.includes(n.id) && n.type === 'preset',
      );
      const generateNode = currentNodes.find((n) => n.id === nodeId);
      const presetData = presetNode?.data as Record<string, unknown> | undefined;
      const generateData = generateNode?.data as Record<string, unknown> | undefined;
      const inlineSettings = generateData?.inlineSettings as Record<string, unknown> | undefined;
      const mediaType = presetData?.mediaType || inlineSettings?.mediaType || 'image';
      const isVideo = mediaType === 'video';
      const isText = mediaType === 'text';

      // Set generating status locally
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, status: 'generating', mediaType } } : n,
        ),
      );

      try {
        // Resolve model ID for last-used tracking
        const modelId = (presetData?.model || inlineSettings?.model) as string | undefined;

        if (isText) {
          const { data } = await canvasService.generateText(canvasId, nodeId);
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      status: 'complete',
                      textContent: data.textContent,
                      imageUrl: null,
                      videoUrl: null,
                      mediaType: 'text',
                      error: null,
                    },
                  }
                : n,
            ),
          );
          if (modelId) useLastUsedModelStore.getState().setLastUsed('text', modelId);
        } else if (isVideo) {
          const { data } = await canvasService.generateVideo(canvasId, nodeId);
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      status: 'complete',
                      videoUrl: data.videoUrl,
                      thumbnailUrl: data.thumbnailUrl,
                      duration: data.duration,
                      imageUrl: null,
                      textContent: null,
                      mediaType: 'video',
                      error: null,
                    },
                  }
                : n,
            ),
          );
          if (modelId) useLastUsedModelStore.getState().setLastUsed('video', modelId);
        } else {
          const { data } = await canvasService.generate(canvasId, nodeId);
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      status: 'complete',
                      imageUrl: data.imageUrl,
                      videoUrl: null,
                      textContent: null,
                      mediaType: 'image',
                      error: null,
                    },
                  }
                : n,
            ),
          );
          if (modelId) useLastUsedModelStore.getState().setLastUsed('image', modelId);
        }
        // Refresh credit balance after successful generation
        useCreditStore.getState().reset();
        useCreditStore.getState().fetchBalance();
      } catch (err: any) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, status: 'error', error: err.message } }
              : n,
          ),
        );
      }
    },
    [canvasId, reactFlowInstance],
  );

  // Handle stop generation — resets node to idle state
  const handleStopGeneration = useCallback((nodeId: string) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, status: 'idle' } } : n)),
    );
  }, []);

  // Handle file upload — uploads to R2 via the API
  const handleUpload = useCallback(
    async (nodeId: string, file: File, dataUrl: string) => {
      // Show local preview immediately while uploading
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: { ...n.data, imageUrl: dataUrl, originalName: file.name, uploading: true },
              }
            : n,
        ),
      );

      try {
        const { data } = await canvasService.uploadFile(canvasId, nodeId, file);
        // Replace local preview with the R2 URL
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, imageUrl: data.imageUrl, uploading: false } }
              : n,
          ),
        );
      } catch (err) {
        console.error('Failed to upload file:', err);
        // Keep local preview but mark upload as failed
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, uploading: false, uploadError: (err as Error).message } }
              : n,
          ),
        );
      }
    },
    [canvasId],
  );

  // Handle selection from gallery — sets image URL directly without upload
  const handleSelectFromGallery = useCallback(async (nodeId: string, imageUrl: string, title: string) => {
    // Update local state immediately
    setNodes((nds) =>
      nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, imageUrl, originalName: title } } : n))
    );

    // Persist to backend
    try {
      await canvasService.updateNode(canvasId, nodeId, {
        data: { imageUrl, originalName: title },
      });
    } catch (err) {
      console.error('Failed to save gallery selection:', err);
    }
  }, [canvasId]);

  // Handle new connections
  const onConnect = useCallback(
    async (connection: Connection) => {
      const newEdge: Edge = {
        ...connection,
        id: `edge-${Date.now()}`,
        source: connection.source!,
        target: connection.target!,
        type: 'deletable',
        animated: true,
        style: { stroke: '#10b981', strokeWidth: 2 },
        data: { onDelete: handleDeleteEdge },
      };
      setEdges((eds) => addEdge(newEdge, eds));

      // Save to backend
      try {
        const { data } = await canvasService.addEdge(canvasId, {
          source_node_id: connection.source,
          target_node_id: connection.target,
          source_handle: connection.sourceHandle,
          target_handle: connection.targetHandle,
        });
        // Update edge id with server-generated id
        setEdges((eds) => eds.map((e) => (e.id === newEdge.id ? { ...e, id: data.id } : e)));
      } catch (err) {
        console.error('Failed to save edge:', err);
      }
    },
    [canvasId, handleDeleteEdge],
  );

  // Handle edge deletion
  const onEdgesDelete = useCallback(
    async (deletedEdges: Edge[]) => {
      for (const edge of deletedEdges) {
        // Prevent deletion of variation bonds
        if (edge.type === 'variation' || edge.data?.isVariationBond) {
          // Re-add the edge — cannot be broken
          setEdges((eds) => {
            if (eds.find((e) => e.id === edge.id)) return eds;
            return [...eds, edge];
          });
          continue;
        }
        try {
          await canvasService.deleteEdge(canvasId, edge.id);
        } catch (err) {
          console.error('Failed to delete edge:', err);
        }
      }
    },
    [canvasId],
  );

  // Handle node deletion (keyboard Delete key)
  const onNodesDelete = useCallback(async (deletedNodes: Node[]) => {
    // Collect variation children that should also be deleted
    const deletedIds = new Set(deletedNodes.map((n) => n.id));
    const variationChildrenToDelete = nodesRef.current.filter(
      (n) => n.type === 'variation' && n.data?.parentNodeId && deletedIds.has(n.data.parentNodeId as string) && !deletedIds.has(n.id)
    );

    // Capture all nodes and edges for undo before deletion
    const allDeletedNodes = [...deletedNodes, ...variationChildrenToDelete];
    const allDeletedIds = new Set(allDeletedNodes.map(n => n.id));
    const affectedEdges = edgesRef.current.filter(
      e => allDeletedIds.has(e.source) || allDeletedIds.has(e.target)
    );

    // Store node data for restoration (strip callbacks)
    const nodesToRestore = allDeletedNodes.map(n => ({
      id: n.id,
      type: n.type,
      position: { ...n.position },
      data: {
        ...n.data,
        canvasId: undefined,
        onUpdate: undefined,
        onGenerate: undefined,
        onStopGeneration: undefined,
        onUpload: undefined,
        onSelectFromGallery: undefined,
        onDelete: undefined,
        onConvertToPreset: undefined,
        onWidthChange: undefined,
        onAddVariation: undefined,
        onGenerateVariation: undefined,
      },
    }));
    const edgesToRestore = affectedEdges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      type: e.type,
    }));

    // Push undo action - reload canvas after restore to reinitialize callbacks
    pushAction({
      type: 'nodes-delete',
      description: `Delete ${allDeletedNodes.length} node(s)`,
      undo: async () => {
        // Re-create nodes
        for (const node of nodesToRestore) {
          try {
            await canvasService.addNode(canvasId, {
              type: node.type,
              position_x: node.position.x,
              position_y: node.position.y,
              data: node.data,
            });
          } catch (err) {
            console.error('Failed to restore node:', err);
          }
        }
        // Re-create edges
        for (const edge of edgesToRestore) {
          try {
            await canvasService.addEdge(canvasId, {
              source_node_id: edge.source,
              target_node_id: edge.target,
              source_handle: edge.sourceHandle,
              target_handle: edge.targetHandle,
            });
          } catch (err) {
            console.error('Failed to restore edge:', err);
          }
        }
        // Reload canvas to get proper callbacks
        reloadCanvasQuietlyRef.current?.();
      },
      redo: async () => {
        // Delete all nodes again
        for (const node of nodesToRestore) {
          try {
            await canvasService.deleteNode(canvasId, node.id);
          } catch {
            // Node may already be deleted or have new ID
          }
        }
        // Reload canvas to sync state
        reloadCanvasQuietlyRef.current?.();
      },
    });

    // Perform the deletion
    for (const child of variationChildrenToDelete) {
      try {
        await canvasService.deleteNode(canvasId, child.id);
      } catch (err) {
        console.error('Failed to delete variation child:', err);
      }
    }
    if (variationChildrenToDelete.length > 0) {
      const childIds = new Set(variationChildrenToDelete.map((c) => c.id));
      setNodes((nds) => nds.filter((n) => !childIds.has(n.id)));
      setEdges((eds) => eds.filter((e) => !childIds.has(e.source) && !childIds.has(e.target)));
    }

    for (const node of deletedNodes) {
      try {
        await canvasService.deleteNode(canvasId, node.id);
      } catch (err) {
        console.error('Failed to delete node:', err);
      }
    }
  }, [canvasId, pushAction]);

  // Handle node deletion via button (for mobile)
  // When deleting a generate node, also cascade-delete its variation children.
  const handleDeleteNode = useCallback(async (nodeId: string) => {
    try {
      // Find the node to delete
      const nodeToDelete = nodesRef.current.find(n => n.id === nodeId);
      if (!nodeToDelete) return;

      // Find variation children that are permanently bonded to this node
      const variationChildren = nodesRef.current.filter(
        (n) => n.type === 'variation' && n.data?.parentNodeId === nodeId
      );

      // Capture all nodes and edges for undo before deletion
      const allDeletedNodes = [nodeToDelete, ...variationChildren];
      const allDeletedIds = new Set(allDeletedNodes.map(n => n.id));
      const affectedEdges = edgesRef.current.filter(
        e => allDeletedIds.has(e.source) || allDeletedIds.has(e.target)
      );

      // Store node data for restoration (strip callbacks)
      const nodesToRestore = allDeletedNodes.map(n => ({
        id: n.id,
        type: n.type,
        position: { ...n.position },
        data: {
          ...n.data,
          canvasId: undefined,
          onUpdate: undefined,
          onGenerate: undefined,
          onStopGeneration: undefined,
          onUpload: undefined,
          onSelectFromGallery: undefined,
          onDelete: undefined,
          onConvertToPreset: undefined,
          onWidthChange: undefined,
          onAddVariation: undefined,
          onGenerateVariation: undefined,
        },
      }));
      const edgesToRestore = affectedEdges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        type: e.type,
      }));

      // Push undo action
      pushAction({
        type: 'node-delete',
        description: `Delete node`,
        undo: async () => {
          // Re-create nodes
          for (const node of nodesToRestore) {
            try {
              await canvasService.addNode(canvasId, {
                type: node.type,
                position_x: node.position.x,
                position_y: node.position.y,
                data: node.data,
              });
            } catch (err) {
              console.error('Failed to restore node:', err);
            }
          }
          // Re-create edges
          for (const edge of edgesToRestore) {
            try {
              await canvasService.addEdge(canvasId, {
                source_node_id: edge.source,
                target_node_id: edge.target,
                source_handle: edge.sourceHandle,
                target_handle: edge.targetHandle,
              });
            } catch (err) {
              console.error('Failed to restore edge:', err);
            }
          }
          // Reload canvas to get proper callbacks
          reloadCanvasQuietlyRef.current?.();
        },
        redo: async () => {
          // Delete all nodes again
          for (const node of nodesToRestore) {
            try {
              await canvasService.deleteNode(canvasId, node.id);
            } catch {
              // Node may already be deleted or have new ID
            }
          }
          // Reload canvas to sync state
          reloadCanvasQuietlyRef.current?.();
        },
      });

      // Delete variation children first
      for (const child of variationChildren) {
        try {
          await canvasService.deleteNode(canvasId, child.id);
        } catch (err) {
          console.error('Failed to delete variation child:', err);
        }
      }

      await canvasService.deleteNode(canvasId, nodeId);

      const deletedIds = new Set([nodeId, ...variationChildren.map((c) => c.id)]);
      setNodes((nds) => nds.filter((n) => !deletedIds.has(n.id)));
      setEdges((eds) => eds.filter((e) => !deletedIds.has(e.source) && !deletedIds.has(e.target)));
    } catch (err) {
      console.error('Failed to delete node:', err);
    }
  }, [canvasId, pushAction]);

  // Convert inline settings from a Generate node into a new Preset node
  const handleConvertToPreset = useCallback(
    async (generateNodeId: string, inlineSettings: any) => {
      // Find the generate node to get its position (use ref for fresh state)
      const generateNode = nodesRef.current.find((n) => n.id === generateNodeId);
      if (!generateNode) {
        console.error('Could not find generate node:', generateNodeId);
        return;
      }

      // Create a new preset node positioned to the left of the generate node
      const presetPosition = {
        x: generateNode.position.x - 280,
        y: generateNode.position.y,
      };

      // Build preset data from inline settings
      const presetData = {
        mediaType: inlineSettings.mediaType || 'image',
        model: inlineSettings.model,
        size: inlineSettings.size,
        aspectRatio: inlineSettings.aspectRatio,
        duration: inlineSettings.duration,
        resolution: inlineSettings.resolution,
        modelSettings: inlineSettings.modelSettings,
      };

      try {
        // Create the preset node
        const { data: newPresetData } = await canvasService.addNode(canvasId, {
          type: 'preset',
          position_x: presetPosition.x,
          position_y: presetPosition.y,
          data: presetData,
        });

        const newPresetNode: Node = {
          id: newPresetData.id,
          type: 'preset',
          position: presetPosition,
          data: {
            ...newPresetData.data,
            canvasId,
            onUpdate: handleNodeDataUpdate,
            onDelete: handleDeleteNode,
            onWidthChange: handleWidthChange,
          },
        };

        setNodes((nds) => [...nds, newPresetNode]);

        // Create an edge connecting the preset to the generate node
        const newEdge = {
          id: `edge-${Date.now()}`,
          source: newPresetData.id,
          target: generateNodeId,
          sourceHandle: 'preset-source',
          targetHandle: 'generate-target',
          type: 'deletable',
          animated: true,
          style: { stroke: '#10b981', strokeWidth: 2 },
          data: { onDelete: handleDeleteEdge },
        };

        setEdges((eds) => [...eds, newEdge]);

        // Save edge to backend
        const { data: savedEdge } = await canvasService.addEdge(canvasId, {
          source_node_id: newPresetData.id,
          target_node_id: generateNodeId,
          source_handle: 'preset-source',
          target_handle: 'generate-target',
        });

        // Update edge with server-generated id
        setEdges((eds) => eds.map((e) => (e.id === newEdge.id ? { ...e, id: savedEdge.id } : e)));

        // Clear inline settings from the generate node (preset now provides settings)
        await canvasService.updateNode(canvasId, generateNodeId, {
          data: { ...generateNode.data, inlineSettings: undefined },
        });
        setNodes((nds) =>
          nds.map((n) =>
            n.id === generateNodeId ? { ...n, data: { ...n.data, inlineSettings: undefined } } : n,
          ),
        );
      } catch (err) {
        console.error('Failed to convert to preset:', err);
      }
    },
    [canvasId, handleNodeDataUpdate, handleDeleteNode, handleDeleteEdge, handleWidthChange],
  );

  // Quiet reload function that doesn't show loading spinner (preserves viewport for undo/redo)
  useEffect(() => {
    reloadCanvasQuietlyRef.current = async () => {
      try {
        const { data } = await canvasService.get(canvasId);

        const flowNodes: Node[] = data.nodes.map((n: any) => ({
          id: n.id,
          type: n.type,
          position: { x: n.position_x, y: n.position_y },
          data: {
            ...n.data,
            // Clear stale uploading flag if node already has an imageUrl
            uploading: n.data?.uploading && !n.data?.imageUrl ? true : false,
            nodeWidth: n.width || undefined,
            canvasId,
            onUpdate: handleNodeDataUpdate,
            onGenerate: handleGenerate,
            onStopGeneration: handleStopGeneration,
            onUpload: handleUpload,
            onSelectFromGallery: handleSelectFromGallery,
            onDelete: handleDeleteNode,
            onConvertToPreset: handleConvertToPreset,
            onWidthChange: handleWidthChange,
            ...(n.type === 'generate' ? { onAddVariation: handleAddVariation, onSaveAsTemplate: handleSaveAsTemplate } : {}),
            ...(n.type === 'variation' ? { onGenerateVariation: handleGenerateVariation } : {}),
            ...(n.type === 'template' ? { onGenerateTemplate: handleGenerateTemplate } : {}),
          },
        }));

        const variationNodeIds = new Set(data.nodes.filter((n: any) => n.type === 'variation').map((n: any) => n.id));
        const flowEdges: Edge[] = data.edges.map((e: any) => {
          const isVariationBond = variationNodeIds.has(e.target_node_id) && e.target_handle === 'variation-target';
          return {
            id: e.id,
            source: e.source_node_id,
            target: e.target_node_id,
            sourceHandle: e.source_handle,
            targetHandle: e.target_handle,
            type: isVariationBond ? 'variation' : 'deletable',
            animated: !isVariationBond,
            style: isVariationBond ? undefined : { stroke: '#10b981', strokeWidth: 2 },
            data: isVariationBond ? { isVariationBond: true } : { onDelete: handleDeleteEdge },
          };
        });

        setNodes(flowNodes);
        setEdges(flowEdges);
      } catch (err) {
        console.error('Failed to reload canvas:', err);
      }
    };
  }, [canvasId, handleNodeDataUpdate, handleGenerate, handleStopGeneration, handleUpload, handleDeleteNode, handleConvertToPreset, handleWidthChange, handleAddVariation, handleGenerateVariation, handleGenerateTemplate, handleSaveAsTemplate, handleDeleteEdge]);

  // Add a new node
  const handleAddNode = useCallback(
    async (type: NodeType) => {
      // Place new node at center of visible viewport
      let position = { x: 250, y: 150 };
      try {
        const container = document.querySelector('.react-flow');
        if (container) {
          const rect = container.getBoundingClientRect();
          position = reactFlowInstance.screenToFlowPosition({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          });
          // Add small random offset to avoid stacking nodes exactly on top of each other
          position.x += (Math.random() - 0.5) * 50;
          position.y += (Math.random() - 0.5) * 50;
        }
      } catch {
        // Fallback to default position if viewport calculation fails
      }

      const defaultData: Record<NodeType, any> = {
        prompt: { text: '' },
        upload: {},
        preset: {
          mediaType: 'image',
          size: '1K',
          aspectRatio: '1:1',
          model: 'gemini-2.5-flash-image',
        },
        generate: { status: 'idle' },
        image: {},
        note: { text: 'this is for ideas' },
        variation: { status: 'idle', variationPrompt: '' },
        template: { status: 'idle' },
      };

      try {
        const { data } = await canvasService.addNode(canvasId, {
          type,
          position_x: position.x,
          position_y: position.y,
          data: defaultData[type],
        });

        const newNode: Node = {
          id: data.id,
          type,
          position,
          data: {
            ...data.data,
            canvasId,
            onUpdate: handleNodeDataUpdate,
            onGenerate: handleGenerate,
            onStopGeneration: handleStopGeneration,
            onUpload: handleUpload,
            onSelectFromGallery: handleSelectFromGallery,
            onDelete: handleDeleteNode,
            onConvertToPreset: handleConvertToPreset,
            onWidthChange: handleWidthChange,
            ...(type === 'generate' ? { onAddVariation: handleAddVariation, onSaveAsTemplate: handleSaveAsTemplate } : {}),
          },
        };

        setNodes((nds) => [...nds, newNode]);
      } catch (err) {
        console.error('Failed to add node:', err);
      }
    },
    [
      canvasId,
      handleNodeDataUpdate,
      handleGenerate,
      handleStopGeneration,
      handleUpload,
      handleSelectFromGallery,
      handleDeleteNode,
      handleConvertToPreset,
      handleWidthChange,
      handleAddVariation,
      handleSaveAsTemplate,
      reactFlowInstance,
    ],
  );

  // Add a template node
  const handleAddTemplateNode = useCallback(async (template: NodeTemplate) => {
    // Place new node at center of visible viewport
    let position = { x: 250, y: 150 };
    try {
      const container = document.querySelector('.react-flow');
      if (container) {
        const rect = container.getBoundingClientRect();
        position = reactFlowInstance.screenToFlowPosition({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
        position.x += (Math.random() - 0.5) * 50;
        position.y += (Math.random() - 0.5) * 50;
      }
    } catch {
      // Fallback to default position
    }

    try {
      const { data } = await canvasService.addNode(canvasId, {
        type: 'template',
        position_x: position.x,
        position_y: position.y,
        data: {
          template_id: template.id,
          templateName: template.name,
          templateDescription: template.description,
          sourceCanvasId: template.source_canvas_id,
          sourceCanvasName: template.source_canvas_name,
          status: 'idle',
        },
      });

      const newNode: Node = {
        id: data.id,
        type: 'template',
        position,
        data: {
          ...data.data,
          canvasId,
          onUpdate: handleNodeDataUpdate,
          onDelete: handleDeleteNode,
          onWidthChange: handleWidthChange,
          onGenerateTemplate: handleGenerateTemplate,
        },
      };

      setNodes((nds) => [...nds, newNode]);
    } catch (err) {
      console.error('Failed to add template node:', err);
    }
  }, [canvasId, handleNodeDataUpdate, handleDeleteNode, handleWidthChange, handleGenerateTemplate, reactFlowInstance]);

  // Copy selected nodes to clipboard (Ctrl+C)
  const handleCopy = useCallback(() => {
    const selectedNodes = nodes.filter((n) => n.selected);
    if (selectedNodes.length === 0) return;

    const selectedIds = new Set(selectedNodes.map((n) => n.id));

    // Store copies without callbacks (will be re-attached on paste)
    clipboardNodes = selectedNodes.map((n) => ({
      ...n,
      data: { ...n.data },
    }));

    // Store edges between selected nodes for baking preset settings
    clipboardEdges = edges
      .filter((e) => selectedIds.has(e.source) && selectedIds.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
      }));

    // Store edges from external (non-selected) nodes TO selected nodes
    // These will be reconnected to the pasted nodes
    clipboardExternalEdges = edges
      .filter((e) => !selectedIds.has(e.source) && selectedIds.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
      }));

    // Record when nodes were copied - prioritize internal clipboard over system clipboard on paste
    clipboardTimestamp = Date.now();
  }, [nodes, edges]);

  // Helper: create a Media node from a file at a given position
  const createMediaNodeFromFile = useCallback(async (file: File, position: { x: number; y: number }) => {
    const localUrl = URL.createObjectURL(file);
    // Default width for pasted/dropped media nodes (consistent with other node sizes)
    const defaultMediaWidth = 240;

    try {
      // Create node in backend with default width
      const { data } = await canvasService.addNode(canvasId, {
        type: 'upload',
        position_x: position.x,
        position_y: position.y,
        width: defaultMediaWidth,
        data: { imageUrl: localUrl, originalName: file.name, uploading: true },
      });

      // Add to local state with default width
      const newNode: Node = {
        id: data.id,
        type: 'upload',
        position,
        data: {
          ...data.data,
          imageUrl: localUrl,
          originalName: file.name,
          uploading: true,
          nodeWidth: defaultMediaWidth,
          canvasId,
          onUpdate: handleNodeDataUpdate,
          onGenerate: handleGenerate,
          onStopGeneration: handleStopGeneration,
          onUpload: handleUpload,
          onSelectFromGallery: handleSelectFromGallery,
          onDelete: handleDeleteNode,
          onConvertToPreset: handleConvertToPreset,
          onWidthChange: handleWidthChange,
        },
      };
      setNodes((nds) => [...nds, newNode]);

      // Upload file to R2
      const uploadResult = await canvasService.uploadFile(canvasId, data.id, file);

      // Update with permanent URL
      setNodes((nds) =>
        nds.map((n) => (n.id === data.id ? { ...n, data: { ...n.data, imageUrl: uploadResult.data.imageUrl, uploading: false } } : n))
      );
    } catch (err) {
      console.error('Failed to create media node:', err);
    }
  }, [canvasId, handleNodeDataUpdate, handleGenerate, handleStopGeneration, handleUpload, handleSelectFromGallery, handleDeleteNode, handleConvertToPreset, handleWidthChange, setNodes]);

  // Paste nodes from clipboard (Ctrl+V) — also handles image paste from system clipboard
  // Priority: If nodes were copied internally, paste those. Otherwise, check system clipboard for images.
  const handlePaste = useCallback(async () => {
    // If we have nodes in the internal clipboard, prioritize pasting them
    // This ensures that when you copy nodes (Ctrl+C), they take precedence over any images
    // that might be in the system clipboard
    if (clipboardNodes.length > 0 && clipboardTimestamp > 0) {
      // Paste the copied nodes (handled below)
    } else {
      // No internal nodes copied - try to read image from system clipboard
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const item of clipboardItems) {
          const imageType = item.types.find(t => t.startsWith('image/'));
          if (imageType) {
            const blob = await item.getType(imageType);
            const file = new File([blob], `pasted-image-${Date.now()}.${imageType.split('/')[1] || 'png'}`, { type: imageType });

            // Get paste position at center of viewport
            let pastePosition = { x: 100, y: 100 };
            try {
              const container = document.querySelector('.react-flow');
              if (container) {
                const rect = container.getBoundingClientRect();
                pastePosition = reactFlowInstance.screenToFlowPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top + rect.height / 2,
                });
              }
            } catch {
              // Fallback position
            }

            await createMediaNodeFromFile(file, pastePosition);
            return; // Image pasted, done
          }
        }
      } catch {
        // Clipboard API not available or no permission - fall through to node paste
      }
    }

    // Paste copied nodes
    if (clipboardNodes.length === 0) return;

    // Get paste position at center of viewport
    let pastePosition = { x: 100, y: 100 };
    try {
      const container = document.querySelector('.react-flow');
      if (container) {
        const rect = container.getBoundingClientRect();
        pastePosition = reactFlowInstance.screenToFlowPosition({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
      }
    } catch {
      // Fallback position
    }

    // Calculate offset from first copied node to paste position
    const firstNode = clipboardNodes[0];
    const offsetX = pastePosition.x - firstNode.position.x + 50;
    const offsetY = pastePosition.y - firstNode.position.y + 50;

    // Build a map of clipboard nodes by id for preset lookup
    const clipboardNodeMap = new Map(clipboardNodes.map((n) => [n.id, n]));

    // Helper: find connected preset for a generate node in the clipboard
    const findConnectedPreset = (generateNodeId: string) => {
      for (const edge of clipboardEdges) {
        if (edge.target === generateNodeId) {
          const sourceNode = clipboardNodeMap.get(edge.source);
          if (sourceNode?.type === 'preset') {
            return sourceNode;
          }
        }
      }
      return null;
    };

    // Track mapping from old node IDs to new node IDs
    const oldToNewIdMap = new Map<string, string>();

    // Create new nodes at offset positions (skip variation nodes — they can only be spawned from generate nodes)
    for (const node of clipboardNodes) {
      if (node.type === 'variation') continue;

      const newPosition = {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY,
      };

      // Prepare node data for API (exclude callbacks and transient state)
      const nodeData = { ...node.data };
      delete nodeData.canvasId;
      delete nodeData.onUpdate;
      delete nodeData.onGenerate;
      delete nodeData.onUpload;
      delete nodeData.onSelectFromGallery;
      delete nodeData.onDelete;
      delete nodeData.onConvertToPreset;
      delete nodeData.onWidthChange;
      delete nodeData.onAddVariation;
      delete nodeData.onGenerateVariation;
      delete nodeData.nodeWidth;

      // Reset generate node status and bake in preset settings
      if (node.type === 'generate') {
        nodeData.status = 'idle';
        nodeData.imageUrl = null;
        nodeData.videoUrl = null;
        nodeData.error = null;

        // If this generate node was connected to a preset, bake the settings into inlineSettings
        const connectedPreset = findConnectedPreset(node.id);
        if (connectedPreset && !nodeData.inlineSettings) {
          const presetData = connectedPreset.data;
          nodeData.inlineSettings = {
            mediaType: presetData?.mediaType || 'image',
            model: presetData?.model,
            size: presetData?.size,
            aspectRatio: presetData?.aspectRatio,
            duration: presetData?.duration,
            resolution: presetData?.resolution,
            modelSettings: presetData?.modelSettings,
          };
        }
      }

      try {
        const { data } = await canvasService.addNode(canvasId, {
          type: node.type as any,
          position_x: newPosition.x,
          position_y: newPosition.y,
          data: nodeData,
        });

        // Track the mapping from old ID to new ID
        oldToNewIdMap.set(node.id, data.id);

        const newNode: Node = {
          id: data.id,
          type: node.type,
          position: newPosition,
          data: {
            ...data.data,
            canvasId,
            onUpdate: handleNodeDataUpdate,
            onGenerate: handleGenerate,
            onStopGeneration: handleStopGeneration,
            onUpload: handleUpload,
            onSelectFromGallery: handleSelectFromGallery,
            onDelete: handleDeleteNode,
            onConvertToPreset: handleConvertToPreset,
            onWidthChange: handleWidthChange,
            ...(node.type === 'generate' ? { onAddVariation: handleAddVariation, onSaveAsTemplate: handleSaveAsTemplate } : {}),
          },
        };

        setNodes((nds) => [...nds, newNode]);
      } catch (err) {
        console.error('Failed to paste node:', err);
      }
    }

    // Reconnect external edges to the new pasted nodes
    for (const extEdge of clipboardExternalEdges) {
      const newTargetId = oldToNewIdMap.get(extEdge.target);
      if (!newTargetId) continue;

      // Create edge from the original source node to the new target node
      const newEdge: Edge = {
        id: `edge-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        source: extEdge.source,
        target: newTargetId,
        sourceHandle: extEdge.sourceHandle,
        targetHandle: extEdge.targetHandle,
        type: 'deletable',
        animated: true,
        style: { stroke: '#10b981', strokeWidth: 2 },
        data: { onDelete: handleDeleteEdge },
      };

      setEdges((eds) => [...eds, newEdge]);

      // Save edge to backend
      try {
        const { data: savedEdge } = await canvasService.addEdge(canvasId, {
          source_node_id: extEdge.source,
          target_node_id: newTargetId,
          source_handle: extEdge.sourceHandle,
          target_handle: extEdge.targetHandle,
        });
        // Update edge id with server-generated id
        setEdges((eds) => eds.map((e) => (e.id === newEdge.id ? { ...e, id: savedEdge.id } : e)));
      } catch (err) {
        console.error('Failed to save pasted edge:', err);
      }
    }
  }, [
    canvasId,
    reactFlowInstance,
    handleNodeDataUpdate,
    handleGenerate,
    handleStopGeneration,
    handleUpload,
    handleSelectFromGallery,
    handleDeleteNode,
    handleDeleteEdge,
    handleConvertToPreset,
    handleWidthChange,
    handleAddVariation,
    createMediaNodeFromFile,
  ]);

  // Keyboard shortcuts for copy/paste
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        handlePaste();
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCopy, handlePaste, undo, redo]);

  // --- Real-time sync via WebSocket ---
  const handleSyncEvent = useCallback(
    (event: CanvasSyncEvent) => {
      switch (event.type) {
        case 'node-added':
          setNodes((nds) => {
            if (nds.find((n) => n.id === event.payload.id)) return nds;
            const nodeType = event.payload.type;
            return [
              ...nds,
              {
                id: event.payload.id,
                type: nodeType,
                position: { x: event.payload.position_x, y: event.payload.position_y },
                data: {
                  ...event.payload.data,
                  nodeWidth: event.payload.width || undefined,
                  canvasId,
                  onUpdate: handleNodeDataUpdate,
                  onGenerate: handleGenerate,
                  onStopGeneration: handleStopGeneration,
                  onUpload: handleUpload,
                  onSelectFromGallery: handleSelectFromGallery,
                  onDelete: handleDeleteNode,
                  onConvertToPreset: handleConvertToPreset,
                  onWidthChange: handleWidthChange,
                  ...(nodeType === 'generate' ? { onAddVariation: handleAddVariation, onSaveAsTemplate: handleSaveAsTemplate } : {}),
                  ...(nodeType === 'variation'
                    ? { onGenerateVariation: handleGenerateVariation }
                    : {}),
                  ...(nodeType === 'template' ? { onGenerateTemplate: handleGenerateTemplate } : {}),
                },
              },
            ];
          });
          break;

        case 'node-updated':
          setNodes((nds) =>
            nds.map((n) =>
              n.id === event.payload.nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      ...event.payload.updates?.data,
                      ...(event.payload.updates?.width !== undefined
                        ? { nodeWidth: event.payload.updates.width }
                        : {}),
                    },
                    ...(event.payload.updates?.position_x !== undefined
                      ? {
                          position: {
                            x: event.payload.updates.position_x,
                            y: event.payload.updates.position_y ?? n.position.y,
                          },
                        }
                      : {}),
                  }
                : n,
            ),
          );
          break;

        case 'node-deleted':
          setNodes((nds) => nds.filter((n) => n.id !== event.payload.nodeId));
          setEdges((eds) =>
            eds.filter(
              (e) => e.source !== event.payload.nodeId && e.target !== event.payload.nodeId,
            ),
          );
          break;

        case 'edge-added':
          setEdges((eds) => {
            if (eds.find((e) => e.id === event.payload.id)) return eds;
            const isVariationBond = event.payload.target_handle === 'variation-target';
            return [
              ...eds,
              {
                id: event.payload.id,
                source: event.payload.source_node_id,
                target: event.payload.target_node_id,
                sourceHandle: event.payload.source_handle,
                targetHandle: event.payload.target_handle,
                type: isVariationBond ? 'variation' : 'deletable',
                animated: !isVariationBond,
                style: isVariationBond ? undefined : { stroke: '#10b981', strokeWidth: 2 },
                data: isVariationBond ? { isVariationBond: true } : { onDelete: handleDeleteEdge },
              },
            ];
          });
          break;

        case 'edge-deleted':
          setEdges((eds) => eds.filter((e) => e.id !== event.payload.edgeId));
          break;

        case 'node-moved':
          setNodes((nds) =>
            nds.map((n) => {
              const moved = event.payload.nodes?.find((m: any) => m.id === n.id);
              return moved ? { ...n, position: { x: moved.position_x, y: moved.position_y } } : n;
            }),
          );
          break;

        case 'node-dragging': {
          const { userId: dragUserId, nodes: draggedNodes, finished } = event.payload;
          const draggedNodeIds = new Set((draggedNodes || []).map((d: any) => d.id));
          // Update node positions and visual indicators in real-time
          setNodes((nds) =>
            nds.map((n) => {
              if (!draggedNodeIds.has(n.id)) {
                // If this node was previously dragged by this user but is no longer in the list, clear it
                if (finished && n.data?._remoteDragUserId === dragUserId) {
                  return {
                    ...n,
                    className: undefined,
                    data: { ...n.data, _remoteDragUserId: undefined },
                  };
                }
                return n;
              }
              const dragged = draggedNodes.find((d: any) => d.id === n.id);
              if (finished) {
                return {
                  ...n,
                  position: { x: dragged.position_x, y: dragged.position_y },
                  className: undefined,
                  data: { ...n.data, _remoteDragUserId: undefined },
                };
              }
              return {
                ...n,
                position: { x: dragged.position_x, y: dragged.position_y },
                className: 'remote-dragging',
                data: { ...n.data, _remoteDragUserId: dragUserId },
              };
            }),
          );
          // Update remote drag tracking ref
          if (finished) {
            for (const [nodeId, uid] of remoteDragsRef.current) {
              if (uid === dragUserId) remoteDragsRef.current.delete(nodeId);
            }
          } else {
            for (const d of draggedNodes || []) {
              remoteDragsRef.current.set(d.id, dragUserId);
            }
          }
          break;
        }
      }
    },
    [
      handleNodeDataUpdate,
      handleGenerate,
      handleUpload,
      handleSelectFromGallery,
      handleDeleteNode,
      handleDeleteEdge,
      handleConvertToPreset,
      handleWidthChange,
      handleAddVariation,
      handleGenerateVariation,
      handleGenerateTemplate,
      handleSaveAsTemplate,
    ],
  );

  const wsRef = useCanvasSync(canvasId, handleSyncEvent, user?.id, user?.fullName || user?.email);

  // Capture positions when drag starts (for undo)
  const onNodeDragStart = useCallback((_: React.MouseEvent, node: Node, draggedNodes: Node[]) => {
    const nodesToTrack = draggedNodes.length > 0 ? draggedNodes : [node];
    dragStartPositionsRef.current.clear();
    nodesToTrack.forEach(n => {
      dragStartPositionsRef.current.set(n.id, { x: n.position.x, y: n.position.y });
    });
  }, []);

  // Broadcast node positions via WS while dragging (throttled to 20Hz)
  const lastDragSentRef = useRef(0);
  const onNodeDrag = useCallback(
    (event: React.MouseEvent, node: Node, nodes: Node[]) => {
      const now = Date.now();
      if (now - lastDragSentRef.current < 50) return;
      lastDragSentRef.current = now;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !user?.id) return;

      // Send all dragged nodes (supports multi-select drag)
      const draggedNodes = nodes.length > 0 ? nodes : [node];
      wsRef.current.send(
        JSON.stringify({
          type: 'node-dragging',
          payload: {
            userId: user.id,
            nodes: draggedNodes.map((n) => ({
              id: n.id,
              position_x: n.position.x,
              position_y: n.position.y,
            })),
          },
        }),
      );

      // Also broadcast cursor position during drag (mousemove on the wrapper
      // doesn't fire while React Flow handles the drag internally)
      lastCursorSentRef.current = now;
      const flowPos = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      wsRef.current.send(
        JSON.stringify({
          type: 'cursor-moved',
          payload: { userId: user.id, x: flowPos.x, y: flowPos.y },
        }),
      );
    },
    [wsRef, user?.id, reactFlowInstance],
  );

  // Handle node drag end - save positions and broadcast final position
  const onNodeDragStop = useCallback(
    async (_: any, node: Node, nodes: Node[]) => {
      const draggedNodes = nodes.length > 0 ? nodes : [node];

      // Broadcast drag-end so other clients clear the dragging indicator
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && user?.id) {
        wsRef.current.send(
          JSON.stringify({
            type: 'node-dragging',
            payload: {
              userId: user.id,
              finished: true,
              nodes: draggedNodes.map((n) => ({
                id: n.id,
                position_x: n.position.x,
                position_y: n.position.y,
              })),
            },
          }),
        );
      }

      // Capture original and new positions for undo
      const originalPositions = new Map<string, { x: number; y: number }>();
      const newPositions = new Map<string, { x: number; y: number }>();
      for (const n of draggedNodes) {
        const orig = dragStartPositionsRef.current.get(n.id);
        if (orig) {
          originalPositions.set(n.id, orig);
          newPositions.set(n.id, { x: n.position.x, y: n.position.y });
        }
      }

      // Only push undo if position actually changed
      const hasMoved = Array.from(originalPositions.entries()).some(([id, orig]) => {
        const newPos = newPositions.get(id);
        return newPos && (Math.abs(orig.x - newPos.x) > 1 || Math.abs(orig.y - newPos.y) > 1);
      });

      if (hasMoved && originalPositions.size > 0) {
        pushAction({
          type: 'node-move',
          description: `Move ${originalPositions.size} node(s)`,
          undo: async () => {
            for (const [nodeId, pos] of originalPositions) {
              setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, position: pos } : n));
              await canvasService.updateNode(canvasId, nodeId, { position_x: pos.x, position_y: pos.y });
            }
          },
          redo: async () => {
            for (const [nodeId, pos] of newPositions) {
              setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, position: pos } : n));
              await canvasService.updateNode(canvasId, nodeId, { position_x: pos.x, position_y: pos.y });
            }
          },
        });
      }

      // Persist to database
      for (const n of draggedNodes) {
        try {
          await canvasService.updateNode(canvasId, n.id, {
            position_x: n.position.x,
            position_y: n.position.y,
          });
        } catch (err) {
          console.error('Failed to save node position:', err);
        }
      }
    },
    [canvasId, wsRef, user?.id, pushAction, setNodes],
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const now = Date.now();
      if (now - lastCursorSentRef.current < 50) return; // throttle to 20Hz
      lastCursorSentRef.current = now;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !user?.id) return;
      const flowPos = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      wsRef.current.send(
        JSON.stringify({
          type: 'cursor-moved',
          payload: { userId: user.id, x: flowPos.x, y: flowPos.y },
        }),
      );
    },
    [wsRef, user?.id, reactFlowInstance],
  );

  const handleCanvasMouseLeave = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !user?.id) return;
    wsRef.current.send(
      JSON.stringify({
        type: 'cursor-moved',
        payload: { userId: user.id, x: null, y: null },
      }),
    );
  }, [wsRef, user?.id]);

  // Canvas-level drag-drop handlers for creating Media nodes from dropped files
  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsCanvasDragOver(true);
    }
  }, []);

  const handleCanvasDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're leaving the canvas entirely (not entering a child)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsCanvasDragOver(false);
    }
  }, []);

  const handleCanvasDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCanvasDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    // Get drop position in flow coordinates
    let dropPosition = { x: 100, y: 100 };
    try {
      dropPosition = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });
    } catch {
      // Fallback position
    }

    // Process each dropped image file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        // Offset each subsequent file to avoid stacking
        const position = {
          x: dropPosition.x + i * 30,
          y: dropPosition.y + i * 30,
        };
        await createMediaNodeFromFile(file, position);
      }
    }
  }, [reactFlowInstance, createMediaNodeFromFile]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading canvas...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full relative"
      onMouseMove={readOnly ? undefined : handleCanvasMouseMove}
      onMouseLeave={readOnly ? undefined : handleCanvasMouseLeave}
      onDragOver={readOnly ? undefined : handleCanvasDragOver}
      onDragLeave={readOnly ? undefined : handleCanvasDragLeave}
      onDrop={readOnly ? undefined : handleCanvasDrop}
    >
      {/* Drop overlay for visual feedback */}
      {!readOnly && isCanvasDragOver && (
        <div className="absolute inset-0 z-50 bg-primary-500/10 border-2 border-dashed border-primary-500 pointer-events-none flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg px-6 py-4 flex items-center gap-3">
            <CloudUpload className="w-8 h-8 text-primary-500" />
            <span className="text-lg font-medium text-primary-600">Drop images to add to canvas</span>
          </div>
        </div>
      )}
      {!readOnly && (
        <CanvasToolbar
          onAddNode={handleAddNode}
          onAddTemplateNode={handleAddTemplateNode}
          onUndo={undo}
          onRedo={redo}
          canUndo={canUndo}
          canRedo={canRedo}
        />
      )}
      {!readOnly && <CursorOverlay users={presenceUsers} />}
      <ReactFlow
        nodes={nodes}
        edges={highlightedEdges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onNodeDragStart={readOnly ? undefined : onNodeDragStart}
        onNodeDrag={readOnly ? undefined : onNodeDrag}
        onNodeDragStop={readOnly ? undefined : onNodeDragStop}
        onEdgesDelete={readOnly ? undefined : onEdgesDelete}
        onNodesDelete={readOnly ? undefined : onNodesDelete}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        deleteKeyCode={readOnly ? null : "Delete"}
        multiSelectionKeyCode={readOnly ? null : "Shift"}
        selectionKeyCode={readOnly ? null : "Control"}
        selectionMode={SelectionMode.Partial}
        proOptions={{ hideAttribution: true }}
        className="bg-gray-50"
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e5e7eb" />
        <Controls className="!bg-white !border-gray-200 !shadow-md" />
        <MiniMap
          pannable
          zoomable
          nodeColor={(node) => {
            switch (node.type) {
              case 'prompt':
                return '#10b981';
              case 'image':
                return '#a18072';
              case 'preset':
                return '#3b82f6';
              case 'upload':
                return '#f59e0b';
              case 'generate':
                return '#10b981';
              case 'variation':
                return '#f59e0b';
              default:
                return '#9ca3af';
            }
          }}
          className="!bg-white !border-gray-200 !shadow-md !hidden md:!block"
        />
      </ReactFlow>

      {/* Paywall overlay for free/exhausted users */}
      <CanvasPaywallOverlay />

      {/* Save as Template Modal */}
      <SaveAsTemplateModal
        isOpen={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setTemplateModalNodeId(null);
        }}
        canvasId={canvasId}
        nodeId={templateModalNodeId || ''}
        onSuccess={handleTemplateSaved}
      />
    </div>
  );
}
